import { isValidEmail } from "../../../../utils/email";

/**
 * Send Invoice (Roadmap 1789, owner 2026-10-21): rules for the "Send from ProCharted
 * (no-reply)" email. The backend (`POST api/invoice/{id}/send-no-reply`) checks the
 * same limits again and always adds the no-reply footer itself.
 */

export const MAX_RECIPIENTS = 10;
export const MAX_SUBJECT = 200;
export const MAX_MESSAGE = 10000;
export const MAX_PDF_BYTES = 10 * 1024 * 1024;

export type SendMethod = "noReply" | "mailbox";

export type NoReplyPayload = {
  to: string[];
  subject: string;
  message: string;
  pdfBase64: string;
  fileName: string;
};

type EmailList = { emailAddress?: string | null }[] | null | undefined;

/** The minimum the helpers read from an invoice (IInvoiceView). */
export type InvoiceForEmail = {
  invoiceNo?: number | null;
  total?: number | null;
  balance?: number | null;
  dueDate?: string | null;
  customer?: {
    name?: string | null;
    contact?: { email?: EmailList; emails?: EmailList } | null;
    businessContact?: { emails?: EmailList; email?: EmailList } | null;
  } | null;
};

export type FirmForEmail = {
  displayName?: string | null;
  legalName?: string | null;
} | null;

/** Same rule as the server (shared check, QA batch 12). */
export { isValidEmail };

/** The firm's name as clients know it (display name, else legal name). */
export const firmDisplayName = (firm: FirmForEmail): string =>
  firm?.displayName?.trim() || firm?.legalName?.trim() || "your accountant";

/** The fixed last line of every no-reply invoice email (the server adds it too). */
export const noReplyFooter = (firmName: string): string =>
  `This message was sent from a no-reply address. Replies to this email are not read — please contact ${firmName} directly.`;

/** Adds addresses without duplicates (ignoring case), keeping the order. */
export const addUnique = (list: string[], values: string[]): string[] => {
  const out = [...list];
  values.forEach((raw) => {
    const value = raw.trim();
    if (value && !out.some((x) => x.toLowerCase() === value.toLowerCase()))
      out.push(value);
  });
  return out;
};

/** The client's email addresses on file (individual or organization), at most 10. */
export const invoiceRecipients = (
  invoice?: InvoiceForEmail | null,
): string[] => {
  const customer = invoice?.customer;
  const lists: EmailList[] = [
    customer?.contact?.email,
    customer?.contact?.emails,
    customer?.businessContact?.emails,
    customer?.businessContact?.email,
  ];
  const found = lists.flatMap((list) =>
    (list || []).map((x) => x?.emailAddress || "").filter(isValidEmail),
  );
  return addUnique([], found).slice(0, MAX_RECIPIENTS);
};

/** Splits typed/pasted text ("a@x.com, b@y.com; c@z.com") into addresses. */
export const splitRecipientInput = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((x) => x.trim().replace(/^<|>$/g, ""))
    .filter(Boolean);

const money = (value?: number | null) =>
  `$${(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const longDate = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const defaultInvoiceSubject = (
  invoice: InvoiceForEmail | null | undefined,
  firmName: string,
): string => `Invoice #${invoice?.invoiceNo ?? ""} from ${firmName}`;

export const defaultInvoiceMessage = (
  invoice: InvoiceForEmail | null | undefined,
  firmName: string,
): string => {
  const name = invoice?.customer?.name?.trim();
  const due = longDate(invoice?.dueDate);
  const amount =
    invoice?.balance != null && invoice.balance > 0
      ? invoice.balance
      : invoice?.total;
  const lines = [
    name ? `Hi ${name},` : "Hello,",
    "",
    `Please find attached invoice #${invoice?.invoiceNo ?? ""} for ${money(
      amount,
    )}${due ? `, due on ${due}` : ""}.`,
    "",
    "Thank you for your business.",
    "",
    firmName,
  ];
  return lines.join("\n");
};

/** Plain-English problem with the form, or null when it can be sent. */
export const noReplyProblem = (form: {
  to: string[];
  subject: string;
  message: string;
  pendingInput?: string;
}): string | null => {
  const pending = splitRecipientInput(form.pendingInput || "");
  const to = addUnique(form.to, pending);
  const bad = to.find((x) => !isValidEmail(x));
  if (bad) return `"${bad}" isn't a valid email address.`;
  if (to.length === 0) return "Add at least one email address.";
  if (to.length > MAX_RECIPIENTS)
    return `An invoice can be sent to at most ${MAX_RECIPIENTS} addresses at once.`;
  if (!form.subject.trim()) return "Add a subject.";
  if (form.subject.trim().length > MAX_SUBJECT)
    return `The subject can be at most ${MAX_SUBJECT} characters.`;
  if (form.message.length > MAX_MESSAGE)
    return `The message can be at most ${MAX_MESSAGE.toLocaleString(
      "en-US",
    )} characters.`;
  return null;
};

/** Bytes of a base64 string (without decoding it). */
export const base64Bytes = (base64: string): number => {
  const clean = base64.replace(/\s/g, "");
  const padding = clean.endsWith("==") ? 2 : clean.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((clean.length * 3) / 4) - padding);
};

/** The request body for `api/invoice/{id}/send-no-reply`. */
export const buildNoReplyPayload = (args: {
  to: string[];
  pendingInput?: string;
  subject: string;
  message: string;
  pdfBase64: string;
  fileName: string;
}): NoReplyPayload => {
  const pdf = args.pdfBase64.includes(",")
    ? args.pdfBase64.slice(args.pdfBase64.indexOf(",") + 1)
    : args.pdfBase64;
  return {
    to: addUnique(args.to, splitRecipientInput(args.pendingInput || "")),
    subject: args.subject.replace(/[\r\n]+/g, " ").trim(),
    message: args.message.replace(/\r\n/g, "\n").trim(),
    pdfBase64: pdf,
    fileName: args.fileName,
  };
};

export const sentToast = (to: string[]): string =>
  to.length === 1
    ? `Invoice emailed to ${to[0]}`
    : `Invoice emailed to ${to.length} addresses`;
